import React, { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState } from 'react';
import { collection, onSnapshot } from 'firebase/firestore';
import { DisconnectReason, Room, RoomEvent, Track } from 'livekit-client';
import { playVoiceCue } from './voiceSounds';
import { createNoiseFilter, noiseAudioContext, noiseFilterSupported, readNoiseFilterPreference, saveNoiseFilterPreference } from './noiseFilter';

const VoiceContext = createContext(null);

// Protobuf TrackSource.SCREEN_SHARE; livekit-client exposes permissions with the numeric enum.
const PROTO_SCREEN_SHARE = 3;
const PRESENCE_INTERVAL_MS = 15000;

export const screenShareSupported = typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getDisplayMedia;

function parseMetadata(raw) {
    try { return JSON.parse(raw || '{}') || {}; } catch (_) { return {}; }
}

function participantState(participant, isLocal, speakers) {
    const meta = parseMetadata(participant.metadata);
    const mic = participant.getTrackPublication(Track.Source.Microphone);
    const screen = participant.getTrackPublication(Track.Source.ScreenShare);
    return {
        uid: participant.identity,
        name: participant.name || 'Thành viên',
        avatar: meta.avatar || '',
        role: meta.role || 'member',
        isLocal,
        isSpeaking: speakers.has(participant.identity),
        micMuted: !mic || mic.isMuted,
        screenTrack: screen?.track && !screen.isMuted ? screen.track : null
    };
}

async function applyNoiseFilter(room, enabled) {
    const track = room?.localParticipant.getTrackPublication(Track.Source.Microphone)?.track;
    if (!track) return;
    const active = track.getProcessor()?.name === 'hope-rnnoise';
    if (enabled && !active) {
        track.setAudioContext(noiseAudioContext());
        await track.setProcessor(createNoiseFilter());
    } else if (!enabled && active) {
        await track.stopProcessor();
    }
}

function localCanShare(room) {
    const permissions = room?.localParticipant?.permissions;
    if (!permissions) return false;
    if (permissions.canPublish === false) return false;
    const sources = permissions.canPublishSources || [];
    return sources.length === 0 || sources.includes(PROTO_SCREEN_SHARE);
}

export function VoiceProvider({ db, auth, appId, currentUid, children }) {
    const [rooms, setRooms] = useState([]);
    const [presence, setPresence] = useState({});
    const [activeRoomId, setActiveRoomId] = useState(null);
    const [connection, setConnection] = useState('idle');
    const [version, setVersion] = useState(0);
    const [micEnabled, setMicEnabled] = useState(false);
    const [deafened, setDeafened] = useState(false);
    const [canPlayAudio, setCanPlayAudio] = useState(true);
    const [noiseFilter, setNoiseFilter] = useState(() => noiseFilterSupported && readNoiseFilterPreference());
    const noiseFilterRef = useRef(noiseFilter);
    const [toasts, setToasts] = useState([]);
    const roomRef = useRef(null);
    const audioElements = useRef(new Map());
    const audioHost = useRef(null);
    const deafenedRef = useRef(false);
    const speakersRef = useRef(new Set());
    const canShareRef = useRef(false);
    const requestKeysRef = useRef(new Set());

    const bump = useCallback(() => setVersion(value => value + 1), []);

    const notify = useCallback((message, tone = 'info') => {
        const id = `${Date.now()}-${Math.random()}`;
        setToasts(list => [...list.slice(-3), { id, message, tone }]);
        window.setTimeout(() => setToasts(list => list.filter(toast => toast.id !== id)), 5000);
    }, []);

    const callApi = useCallback(async (action, payload = {}) => {
        const token = await auth.currentUser?.getIdToken();
        if (!token) throw new Error('Bạn cần đăng nhập lại.');
        const response = await fetch('/api/voice', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
            body: JSON.stringify({ action, ...payload })
        });
        const data = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(data.error || 'Không thể kết nối máy chủ phòng họp.');
        return data;
    }, [auth]);

    useEffect(() => {
        if (!db || !currentUid) return undefined;
        return onSnapshot(collection(db, 'artifacts', appId, 'public', 'data', 'voice_rooms'), snap => {
            const list = snap.docs.map(item => ({ id: item.id, ...item.data() }));
            list.sort((a, b) => (a.createdAt || 0) - (b.createdAt || 0));
            setRooms(list);
        }, error => console.error('[Voice rooms]', error));
    }, [db, appId, currentUid]);

    const refreshPresence = useCallback(async () => {
        try {
            const data = await callApi('presence');
            setPresence(data.presence || {});
        } catch (error) {
            console.warn('[Voice presence]', error.message);
        }
    }, [callApi]);

    const attachAudio = useCallback((track, key) => {
        if (!audioHost.current) {
            audioHost.current = document.createElement('div');
            audioHost.current.style.display = 'none';
            document.body.appendChild(audioHost.current);
        }
        const element = track.attach();
        element.muted = deafenedRef.current;
        audioHost.current.appendChild(element);
        audioElements.current.set(key, { track, element });
    }, []);

    const detachAudio = useCallback((key) => {
        const entry = audioElements.current.get(key);
        if (!entry) return;
        entry.track.detach(entry.element);
        entry.element.remove();
        audioElements.current.delete(key);
    }, []);

    const cleanupRoom = useCallback(() => {
        audioElements.current.forEach((_, key) => detachAudio(key));
        roomRef.current = null;
        speakersRef.current = new Set();
        canShareRef.current = false;
        setActiveRoomId(null);
        setConnection('idle');
        setMicEnabled(false);
        bump();
    }, [bump, detachAudio]);

    const leave = useCallback(async () => {
        const room = roomRef.current;
        if (room) {
            await room.disconnect();
            playVoiceCue('leave');
        }
        cleanupRoom();
        refreshPresence();
    }, [cleanupRoom, refreshPresence]);

    const join = useCallback(async (roomId) => {
        if (roomRef.current && activeRoomId === roomId) return;
        if (roomRef.current) await leave();
        setActiveRoomId(roomId);
        setConnection('connecting');
        try {
            const { token, url, canShare } = await callApi('token', { roomId });
            const room = new Room({
                adaptiveStream: true,
                dynacast: true,
                disconnectOnPageLeave: true,
                // RNNoise replaces the browser's own suppressor; stacking both makes voices sound watery.
                audioCaptureDefaults: { echoCancellation: true, autoGainControl: true, noiseSuppression: !noiseFilterRef.current }
            });
            roomRef.current = room;
            canShareRef.current = canShare;

            room
                .on(RoomEvent.ParticipantConnected, participant => { playVoiceCue('join'); notify(`${participant.name || 'Thành viên'} đã vào phòng`); bump(); })
                .on(RoomEvent.ParticipantDisconnected, () => { playVoiceCue('leave'); bump(); })
                .on(RoomEvent.TrackSubscribed, (track, publication, participant) => {
                    if (track.kind === Track.Kind.Audio) attachAudio(track, `${participant.identity}:${publication.trackSid}`);
                    bump();
                })
                .on(RoomEvent.TrackUnsubscribed, (track, publication, participant) => {
                    detachAudio(`${participant.identity}:${publication.trackSid}`);
                    bump();
                })
                .on(RoomEvent.TrackMuted, bump)
                .on(RoomEvent.TrackUnmuted, bump)
                .on(RoomEvent.TrackPublished, bump)
                .on(RoomEvent.TrackUnpublished, bump)
                .on(RoomEvent.LocalTrackPublished, publication => {
                    if (publication.source === Track.Source.Microphone && noiseFilterRef.current) {
                        applyNoiseFilter(room, true).catch(error => {
                            console.warn('[Voice noise filter]', error);
                            notify('Không bật được lọc tiếng ồn trên thiết bị này.', 'warning');
                        });
                    }
                    bump();
                })
                .on(RoomEvent.LocalTrackUnpublished, (publication) => {
                    if (publication.source === Track.Source.Microphone) setMicEnabled(false);
                    bump();
                })
                .on(RoomEvent.ParticipantMetadataChanged, bump)
                .on(RoomEvent.ParticipantNameChanged, bump)
                .on(RoomEvent.ActiveSpeakersChanged, speakers => {
                    speakersRef.current = new Set(speakers.map(speaker => speaker.identity));
                    bump();
                })
                .on(RoomEvent.ParticipantPermissionsChanged, (_, participant) => {
                    if (participant && participant !== room.localParticipant) return;
                    const next = localCanShare(room);
                    if (next && !canShareRef.current) {
                        playVoiceCue('request');
                        notify('Bạn đã được cho phép chia sẻ màn hình.', 'success');
                    }
                    if (!next && canShareRef.current) notify('Quyền chia sẻ màn hình của bạn đã được thu hồi.', 'warning');
                    canShareRef.current = next;
                    bump();
                })
                .on(RoomEvent.AudioPlaybackStatusChanged, () => setCanPlayAudio(room.canPlaybackAudio))
                .on(RoomEvent.ConnectionStateChanged, state => setConnection(state))
                .on(RoomEvent.Disconnected, reason => {
                    if (roomRef.current !== room) return;
                    if (reason === DisconnectReason.PARTICIPANT_REMOVED) notify('Bạn đã được mời ra khỏi phòng.', 'warning');
                    else if (reason === DisconnectReason.ROOM_DELETED) notify('Phòng đã bị xóa.', 'warning');
                    else if (reason !== DisconnectReason.CLIENT_INITIATED) notify('Mất kết nối phòng họp.', 'warning');
                    cleanupRoom();
                    refreshPresence();
                });

            await room.connect(url, token);
            setCanPlayAudio(room.canPlaybackAudio);
            if (room.localParticipant.permissions) canShareRef.current = localCanShare(room);
            playVoiceCue('join');
            try {
                await room.localParticipant.setMicrophoneEnabled(true);
                setMicEnabled(true);
            } catch (error) {
                notify('Không bật được micro. Hãy cho phép trình duyệt dùng micro.', 'warning');
            }
            setConnection('connected');
            bump();
            refreshPresence();
        } catch (error) {
            if (roomRef.current) await roomRef.current.disconnect();
            cleanupRoom();
            notify(error.message || 'Không vào được phòng.', 'error');
        }
    }, [activeRoomId, attachAudio, bump, callApi, cleanupRoom, detachAudio, leave, notify, refreshPresence]);

    const toggleMic = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        const next = !room.localParticipant.isMicrophoneEnabled;
        try {
            await room.localParticipant.setMicrophoneEnabled(next);
            setMicEnabled(next);
            if (next && deafenedRef.current) {
                deafenedRef.current = false;
                setDeafened(false);
                audioElements.current.forEach(({ element }) => { element.muted = false; });
            }
        } catch (error) {
            notify('Không truy cập được micro.', 'error');
        }
        bump();
    }, [bump, notify]);

    const toggleDeafen = useCallback(async () => {
        const next = !deafenedRef.current;
        deafenedRef.current = next;
        setDeafened(next);
        audioElements.current.forEach(({ element }) => { element.muted = next; });
        const room = roomRef.current;
        if (next && room?.localParticipant.isMicrophoneEnabled) {
            await room.localParticipant.setMicrophoneEnabled(false);
            setMicEnabled(false);
        }
    }, []);

    const toggleScreenShare = useCallback(async () => {
        const room = roomRef.current;
        if (!room) return;
        const next = !room.localParticipant.isScreenShareEnabled;
        try {
            await room.localParticipant.setScreenShareEnabled(next, { audio: true, selfBrowserSurface: 'exclude', surfaceSwitching: 'include', systemAudio: 'include' });
        } catch (error) {
            if (error?.name !== 'NotAllowedError') notify('Không chia sẻ được màn hình. Hãy thử lại hoặc kiểm tra quyền.', 'error');
        }
        bump();
    }, [bump, notify]);

    const toggleNoiseFilter = useCallback(async () => {
        if (!noiseFilterSupported) return;
        const next = !noiseFilterRef.current;
        noiseFilterRef.current = next;
        setNoiseFilter(next);
        saveNoiseFilterPreference(next);
        try {
            await applyNoiseFilter(roomRef.current, next);
        } catch (error) {
            console.warn('[Voice noise filter]', error);
            notify('Không đổi được chế độ lọc tiếng ồn.', 'error');
        }
    }, [notify]);

    const startAudio = useCallback(async () => {
        await roomRef.current?.startAudio();
        setCanPlayAudio(true);
    }, []);

    const runAction = useCallback(async (action, payload, successMessage) => {
        try {
            const data = await callApi(action, payload);
            if (successMessage) notify(successMessage, 'success');
            return data;
        } catch (error) {
            notify(error.message, 'error');
            return null;
        }
    }, [callApi, notify]);

    const activeRoom = rooms.find(room => room.id === activeRoomId) || null;

    // Alert the host when a new screen-share request lands in the room they moderate.
    useEffect(() => {
        const requests = activeRoom?.shareRequests || {};
        const keys = new Set(Object.keys(requests));
        const isHost = activeRoom && activeRoom.ownerUid === currentUid;
        keys.forEach(key => {
            if (!requestKeysRef.current.has(key) && isHost) {
                playVoiceCue('request');
                notify(`${requests[key]?.name || 'Thành viên'} xin chia sẻ màn hình`, 'info');
            }
        });
        requestKeysRef.current = keys;
    }, [activeRoom, currentUid, notify]);

    useEffect(() => () => { roomRef.current?.disconnect(); }, []);

    const participants = useMemo(() => {
        const room = roomRef.current;
        if (!room || connection === 'idle') return [];
        const speakers = speakersRef.current;
        return [
            participantState(room.localParticipant, true, speakers),
            ...Array.from(room.remoteParticipants.values()).map(participant => participantState(participant, false, speakers))
        ];
        // version is the change signal for the mutable LiveKit room.
    }, [version, connection]);

    const value = {
        rooms,
        presence,
        refreshPresence,
        activeRoomId,
        activeRoom,
        connection,
        participants,
        micEnabled,
        deafened,
        canShare: connection === 'connected' && canShareRef.current,
        isSharing: !!roomRef.current?.localParticipant.isScreenShareEnabled,
        canPlayAudio,
        startAudio,
        join,
        leave,
        toggleMic,
        toggleDeafen,
        toggleScreenShare,
        noiseFilter,
        noiseFilterSupported,
        toggleNoiseFilter,
        runAction,
        notify,
        presenceInterval: PRESENCE_INTERVAL_MS
    };

    return (
        <VoiceContext.Provider value={value}>
            {children}
            {toasts.length > 0 && (
                <div className="voice-toasts" role="status" aria-live="polite">
                    {toasts.map(toast => <div key={toast.id} className={`voice-toast voice-toast-${toast.tone}`}>{toast.message}</div>)}
                </div>
            )}
        </VoiceContext.Provider>
    );
}

export function useVoice() {
    return useContext(VoiceContext);
}
